import { FC, useCallback, useRef, useState } from 'react';
import ReactCodeInput from 'react-code-input';

import LoadingScreen from 'components/LoadingScreen/LoadingScreen';
import { verifyOtpByCode } from 'services/APIs/otp';

import styles from './VerificationInput.module.scss';
import CountDownTimer from 'components/VerificationInput/CountDownTimer/CountDownTimer';
import DentalButton from 'components/Buttons/DentalButton/DentalButton';
import { renderToast } from 'components/CustomToast/CustomToast';
import useIsLoadedInsideIframe from 'hooks/useIsLoadedInsideIframe';
import cx from 'classnames';

interface VerificationInputProps {
  onBackClick: () => void;
  phoneNumber: string;
  onCheckedOtpSuccessful: (otpAccessToken: string) => Promise<void>;
}

const VerificationInput: FC<VerificationInputProps> = ({
  onBackClick,
  phoneNumber,
  onCheckedOtpSuccessful,
}) => {
  const inputRef = useRef<any>(null);

  const [isExpired, setIsExpired] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [otpKey, setOtpKey] = useState(0);
  const [otpValue, setOtpValue] = useState('');
  const isLoadedInsideIframe = useIsLoadedInsideIframe();

  const displayErrorToast = useCallback((message: string) => {
    renderToast({ message });
  }, []);

  const handleCheckedOtp = async (input: string) => {
    if (isExpired) {
      setOtpValue('');
      setOtpKey((prev) => prev + 1);
      return displayErrorToast(
        'Verification code expired. Please click on resend.'
      );
    }
    setIsChecked(true);
    try {
      const otpAccessToken = await verifyOtpByCode(phoneNumber, input);
      if (otpAccessToken) {
        await onCheckedOtpSuccessful(otpAccessToken);
      }
    } catch (error) {
      setOtpValue('');
      setOtpKey((prev) => prev + 1);
      displayErrorToast(
        'Invalid verification code. Please enter the correct verification code.'
      );
    }
    setIsChecked(false);
    inputRef.current?.textInput[0].focus();
  };

  return (
    <div className={styles['otp-form']}>
      {isChecked && <LoadingScreen />}
      <div className={styles['verification-card']}>
        <div className={styles['title']}>Enter Verification Code</div>
        <div className={styles['section']}>
          <div>We just sent you a text message.</div>
          <div>Input your code below.</div>
          <ReactCodeInput
            ref={inputRef}
            className={styles['code-input']}
            name="code-input"
            inputMode="numeric"
            type="number"
            fields={6}
            onChange={(value) => {
              setOtpValue(value);
              if (value.length === 6) {
                handleCheckedOtp(value);
              }
            }}
            disabled={isChecked}
            value={otpValue}
            key={otpKey}
          />
          <CountDownTimer
            phoneNumber={phoneNumber}
            onError={displayErrorToast}
            setIsExpired={setIsExpired}
            setIsChecked={setIsChecked}
          />
        </div>
      </div>
      <div
        className={cx(styles['actions'], {
          [styles['actions-iframe']]: isLoadedInsideIframe,
        })}
      >
        <DentalButton variant="outlined" onClick={onBackClick}>
          Back
        </DentalButton>
      </div>
    </div>
  );
};

export default VerificationInput;
